import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clusters from './pages/Clusters';
// import Recommendations from './pages/Recommendations';
import Rightsizing from './pages/Rightsizing';
// import ReservedInstances from './pages/ReservedInstances';
// import PowerScheduling from './pages/PowerScheduling';
// import WorkloadScheduling from './pages/WorkloadScheduling';
import Monitoring from './pages/Monitoring';
import Forecasting from './pages/Forecasting';
import Profile from './pages/Profile';
import Agents from './pages/Agents';
import SuperAdmin from './pages/SuperAdmin';
import ActiveSessions from './pages/ActiveSessions';
import LoginHistory from './pages/LoginHistory';
// import SpotScheduler from './pages/SpotScheduler';
import NodeOptimization from './pages/NodeOptimization';
import Commitments from './pages/Commitments';
import Serverless from './pages/Serverless';
import SchedulerPolicies from './pages/SchedulerPolicies';
import PartnerLogin from './pages/PartnerLogin';
import PartnerDashboard from './pages/PartnerDashboard';
import Docs from './pages/Docs';
import CostReport from './pages/CostReport';
import RealizableSavings from './pages/RealizableSavings';
// Hidden — re-enable alongside the route + sidebar entry.
// import SavingsReport from './pages/SavingsReport';
// Edition split: '@optops-hpc-ui' is a build-time webpack alias (craco.config.js).
// SaaS builds resolve it to src/hpc-stub (enabled=false, null routes) so none of
// the edition code — or its strings — reaches the SaaS bundle; edition builds
// (REACT_APP_EDITION=hpc) resolve to src/hpc. Identifiers here are deliberately
// neutral so nothing edition-specific survives into SaaS output.
import { editionEnabled, editionRoutes, editionHome } from '@optops-hpc-ui';
import GuidedTour from './components/GuidedTour';
import { Toaster } from './components/ui/sonner';
import { ClusterProvider } from './context/ClusterContext';
import { ThemeProvider } from './context/ThemeContext';
import { SavingsProvider } from './context/SavingsContext';
import { TooltipProvider } from './components/ui/tooltip';
import { GoogleOAuthProvider } from '@react-oauth/google';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://dev-api.optops.ai';
export const API = `${BACKEND_URL}/api`;

// Debug: Log API URL in development
if (process.env.NODE_ENV === 'development') {
  console.log('API URL:', API);
}

// Global request timeout (ms). Without this, a slow backend endpoint can hold
// one of the browser's 6 HTTP/1.1 connections to the same origin for the full
// browser default (~5 min), causing every subsequent request to sit in
// "pending" until the connection frees. 30s is generous enough for the slowest
// known endpoints (analyze, dashboard/stats) yet bounded so a single stuck
// call can't lock the connection pool on page refresh.
axios.defaults.timeout = 30000;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios response interceptor for error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log errors for debugging
    console.error('API Error:', error.response?.status, error.response?.data || error.message);

    // Handle 401 - redirect to login. Guard against the reload loop:
    // if we're already on /login, don't redirect again — context-level
    // pollers (e.g. SavingsContext fetches /dashboard/stats on mount)
    // will keep 401-ing until the user actually authenticates, and a
    // hard navigation each time blocks them from ever doing so.
    // Edition builds are on-prem/localhost with no SaaS login flow: background
    // SaaS pollers (SavingsContext etc.) will 401 forever without a token, and
    // a hard redirect to /login would lock the user out of the edition pages.
    if (error.response?.status === 401 && !editionEnabled) {
      const onLogin = window.location.pathname.startsWith('/login');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!onLogin) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPartnerAuthenticated, setIsPartnerAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
    }
    const partnerToken = localStorage.getItem('partner_token');
    if (partnerToken) {
      setIsPartnerAuthenticated(true);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <SavingsProvider>
            <ClusterProvider>
              <Routes>
                <Route
                  path="/signup"
                  element={
                    isAuthenticated ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Login setIsAuthenticated={setIsAuthenticated} />
                    )
                  }
                />
                <Route
                  path="/login"
                  element={
                    isAuthenticated ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Login setIsAuthenticated={setIsAuthenticated} />
                    )
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/clusters"
                  element={
                    isAuthenticated ? <Clusters /> : <Navigate to="/login" replace />
                  }
                />
                {/* <Route
                  path="/recommendations"
                  element={
                    isAuthenticated ? (
                      <Recommendations />
                    ) : (
                      <Navigate to="/login" replace />
                    )
                  }
                /> */}
                <Route
                  path="/rightsizing"
                  element={
                    isAuthenticated ? <Rightsizing /> : <Navigate to="/login" replace />
                  }
                />
                {/* <Route
                  path="/reserved-instances"
                  element={
                    isAuthenticated ? <ReservedInstances /> : <Navigate to="/login" replace />
                  }
                /> */}
                {/* <Route
                  path="/power-scheduling"
                  element={
                    isAuthenticated ? <PowerScheduling /> : <Navigate to="/login" replace />
                  }
                /> */}
                {/* <Route
                  path="/workload-scheduling"
                  element={
                    isAuthenticated ? <WorkloadScheduling /> : <Navigate to="/login" replace />
                  }
                /> */}
                <Route
                  path="/serverless"
                  element={
                    isAuthenticated ? <Serverless /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/monitoring"
                  element={
                    isAuthenticated ? <Monitoring /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/cost-report"
                  element={
                    isAuthenticated ? <CostReport /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/realizable-savings"
                  element={
                    isAuthenticated ? <RealizableSavings /> : <Navigate to="/login" replace />
                  }
                />
                {/* Savings Report hidden — re-enable when the
                    rightsizing/spot/idle math has more validation. */}
                {/* <Route
                  path="/savings-report"
                  element={
                    isAuthenticated ? <SavingsReport /> : <Navigate to="/login" replace />
                  }
                /> */}
                <Route
                  path="/forecasting"
                  element={
                    isAuthenticated ? <Forecasting /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/profile"
                  element={
                    isAuthenticated ? <Profile /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/agents"
                  element={
                    isAuthenticated ? <Agents /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/superadmin"
                  element={
                    isAuthenticated ? (
                      (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role?.toLowerCase() === 'superadmin'; } catch { return false; } })()
                        ? <SuperAdmin />
                        : <Navigate to="/dashboard" replace />
                    ) : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/superadmin/active-sessions"
                  element={
                    isAuthenticated ? (
                      (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role?.toLowerCase() === 'superadmin'; } catch { return false; } })()
                        ? <ActiveSessions />
                        : <Navigate to="/dashboard" replace />
                    ) : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/superadmin/login-history"
                  element={
                    isAuthenticated ? (
                      (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').role?.toLowerCase() === 'superadmin'; } catch { return false; } })()
                        ? <LoginHistory />
                        : <Navigate to="/dashboard" replace />
                    ) : <Navigate to="/login" replace />
                  }
                />
                {/* <Route
                  path="/spot-scheduler"
                  element={
                    isAuthenticated ? <SpotScheduler /> : <Navigate to="/login" replace />
                  }
                /> */}
                <Route
                  path="/node-optimization"
                  element={
                    isAuthenticated ? <NodeOptimization /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/scheduler-policies"
                  element={
                    isAuthenticated ? <SchedulerPolicies /> : <Navigate to="/login" replace />
                  }
                />
                <Route
                  path="/commitments"
                  element={
                    isAuthenticated ? <Commitments /> : <Navigate to="/login" replace />
                  }
                />
                {/* SaaS docs viewer. Gated: in edition builds the edition
                    owns /docs (identical paths -> FIRST route wins in v6, so
                    an ungated SaaS route here shadows the edition page). */}
                {!editionEnabled && (
                  <Route
                    path="/docs"
                    element={<Docs />}
                  />
                )}
                <Route
                  path="/"
                  element={
                    <Navigate
                      to={editionHome || (isAuthenticated ? '/dashboard' : '/login')}
                      replace
                    />
                  }
                />

                {/* Edition routes (null in SaaS builds). Mounted OUTSIDE the
                    auth guard by design: the on-prem PoC serves localhost-only
                    via ssh tunnels and the edition read API is unauthenticated
                    — documented in src/hpc/index.js. */}
                {editionRoutes()}

                {/* Partner Portal Routes */}
                <Route
                  path="/partner/login"
                  element={
                    isPartnerAuthenticated ? <Navigate to="/partner/dashboard" replace /> : <PartnerLogin setIsAuthenticated={setIsPartnerAuthenticated} />
                  }
                />
                <Route
                  path="/partner/dashboard"
                  element={
                    isPartnerAuthenticated ? <PartnerDashboard setIsAuthenticated={setIsPartnerAuthenticated} /> : <Navigate to="/partner/login" replace />
                  }
                />
                <Route
                  path="/partner"
                  element={
                    <Navigate to={isPartnerAuthenticated ? '/partner/dashboard' : '/partner/login'} replace />
                  }
                />
              </Routes>
              {isAuthenticated && <GuidedTour />}
            </ClusterProvider>
          </SavingsProvider>
        </ThemeProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </GoogleOAuthProvider>
  );
}

export default App;